import React, { useState, useEffect, useCallback } from "react";
import { safeFormat } from '@/lib/safeDate';
import { useTranslation } from 'react-i18next';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, UserPlus, Mail, Clock, Phone, Briefcase, Building2, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { createPageUrl } from "@/utils";
import { format } from "date-fns";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAppUsers } from "@/hooks/useAppUsers";
import { useAuth } from "@/hooks/useAuth";
import { getAvatarColor } from "@/lib/utils";
import { SearchBar } from "@/components/common/SearchBar";

export default function UserManagement() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { users, loading, loadUsers } = useAppUsers();
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showInviteDialog, setShowInviteDialog] = useState(false);

  const filterUsers = useCallback(() => {
    if (!searchQuery.trim()) {
      setFilteredUsers(users);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = users.filter(user => {
      const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
      const displayName = user.display_name || '';
      return (
        fullName.toLowerCase().includes(query) ||
        displayName.toLowerCase().includes(query) ||
        user.email?.toLowerCase().includes(query)
      );
    });
    setFilteredUsers(filtered);
  }, [searchQuery, users]);

  // Scroll lock cleanup removed — handled globally by Layout MutationObserver

  useEffect(() => {
    filterUsers();
  }, [filterUsers]);

  const handleViewUser = (userId) => {
    navigate(createPageUrl("UserDetail") + "?id=" + userId);
  };

  if (loading) {
    return (
      <div className="bg-slate-50 p-6 md:p-8">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-64 bg-slate-200 rounded"></div>
          </div>
        </div>
      </div>
    );
  }

  const getDisplayName = (user) => {
    if (user.display_name) return user.display_name;
    const nameParts = [user.first_name, user.last_name].filter(Boolean);
    return nameParts.length > 0 ? nameParts.join(' ') : t('userManagement.defaultUser');
  };

  const getUserInitial = (user) => {
    if (user.first_name) return user.first_name.charAt(0).toUpperCase();
    if (user.display_name) return user.display_name.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <div className="bg-slate-50 p-6 md:p-8">
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => navigate(createPageUrl("Settings"))}
          className="mb-6 text-slate-600 flex items-center gap-2 hover:scale-105 transition-transform duration-200"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('userManagement.backToSettings')}
        </button>

        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">{t('userManagement.title')}</h1>
            <p className="text-slate-600">{t('userManagement.description')}</p>
          </div>
          <Button
            onClick={() => setShowInviteDialog(true)}
            className="bg-blue-600 hover:bg-blue-700"
          >
            <UserPlus className="w-4 h-4 mr-2" />
            {t('userManagement.inviteUser')}
          </Button>
        </div>


        <div className="mb-6 max-w-md">
          <SearchBar
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('userManagement.searchPlaceholder') || "Buscar usuários..."}
          />
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredUsers.map((user) => (
            <Card
              key={user.id}
              onClick={() => handleViewUser(user.id)}
              className="border-slate-200 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className={`w-12 h-12 bg-gradient-to-br ${getAvatarColor(user.first_name || user.display_name)} rounded-full flex items-center justify-center shadow-sm`}>
                    <span className="text-white font-bold text-lg">
                      {getUserInitial(user)}
                    </span>
                  </div>
                  {!user.active && (
                    <Badge variant="outline" className="border-red-200 text-red-700">
                      {t('userManagement.inactive')}
                    </Badge>
                  )}
                </div>

                <h3 className="text-lg font-bold text-slate-900 mb-3">
                  {getDisplayName(user)}
                </h3>

                <div className="space-y-2">
                  {user.email && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Mail className="w-4 h-4 flex-shrink-0" />
                      <span className="truncate">{user.email}</span>
                    </div>
                  )}
                  {user.system_role && (
                    <div className="flex items-center gap-2 text-sm">
                      <Briefcase className="w-4 h-4 flex-shrink-0 text-purple-600" />
                      <Badge variant="outline" className="border-purple-200 text-purple-700 text-xs">
                        {user.system_role.name}
                      </Badge>
                    </div>
                  )}
                  {user.department && (
                    <div className="flex items-center gap-2 text-sm">
                      <Building2 className="w-4 h-4 flex-shrink-0 text-green-600" />
                      <Badge variant="outline" className="border-green-200 text-green-700 text-xs">
                        {user.department.name}
                      </Badge>
                    </div>
                  )}
                  {user.twilio_phone_number && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 font-medium">
                      <Phone className="w-4 h-4 flex-shrink-0" />
                      <span className="font-mono text-xs">{user.twilio_phone_number}</span>
                    </div>
                  )}
                  {user.created_at && (
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Clock className="w-4 h-4 flex-shrink-0" />
                      <span>{t('userManagement.joined')} {safeFormat(new Date(user.created_at), "MMM d, yyyy")}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredUsers.length === 0 && !loading && (
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-12 text-center">
              <Search className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <p className="text-slate-500 text-lg font-medium">{t('userManagement.noUsersFound')}</p>
              <p className="text-sm text-slate-400 mt-2">{t('userManagement.tryAdjustingSearch')}</p>
            </CardContent>
          </Card>
        )}

        {/* Invite User Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('userManagement.inviteNewUser')}</DialogTitle>
              <DialogDescription>
                {t('userManagement.inviteDescription')}
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              <Card className="border-blue-200 bg-blue-50">
                <CardContent className="p-4">
                  <p className="text-sm text-blue-800">
                    {t('userManagement.inviteInstructions')}
                  </p>
                </CardContent>
              </Card>
            </div>
            <DialogFooter>
              <Button onClick={() => setShowInviteDialog(false)}>
                {t('userManagement.gotIt')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
